import React from 'react';
import { DropdownItem, Button as UIButton, UncontrolledTooltip } from 'reactstrap';
import { Link } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { Toolkit } from '../../../../../services/types/Toolkit';
import { CustomDropDown, Button } from '../../../../../shared/components';
import CheckboxList from '../CheckboxList';
import { CheckValue } from '../../../../../services/types/Misc';
import ImpactBadge from '../../../ImpactBadge';
import { Operation } from '../../../../../services/types/Operation';
import RiskBadge from '../../../RiskBadge';
import {
  dateTimeFormat,
  getTypeLinkName,
  tooltipPreventOverflowModifiers,
} from '../../../../../shared/misc/utils';
import EsrrBadge from '../../../EsrrBadge';

interface Props {
  operation: Operation;
  toolkit: Toolkit;
  pdfOptions?: CheckValue[];
  handleChange: (id: string, value: boolean) => void;
  handleDownloadClick: (toolkitId: number) => void;
  handleDeleteClick?: (toolkitId: number) => void;
}

const EspfToolkitVersion = (props: Props) => {
  const { operation, toolkit, pdfOptions, handleChange, handleDownloadClick, handleDeleteClick } =
    props;
  const { t } = useTranslation();
  const isDraft = toolkit.pdfCreatedBy == null;

  const ic = toolkit.impactCategory || toolkit.impactCategoryCalculated;

  return (
    <tr>
      <td>
        {toolkit.isDeleted ? (
          <span className="deleted-toolkit">
            <FontAwesomeIcon icon="copy" />
            {` Toolkit v.${toolkit.version}`}
            <div className="ml-2 toolkit-tooltip" id={`tr-toolkit-version-${toolkit.version}`}>
              <FontAwesomeIcon icon="exclamation-circle" />
            </div>
            <UncontrolledTooltip
              className="tooltip-toolkit"
              placement="bottom"
              target={`tr-toolkit-version-${toolkit.version}`}
              modifiers={tooltipPreventOverflowModifiers}
            >
              {t('documentDeleted')}
            </UncontrolledTooltip>
          </span>
        ) : (
          <Link
            key={`tv_link_to_${toolkit.id}`}
            to={`/operations/${operation.number}/toolkit/espf/form/${getTypeLinkName(
              toolkit.toolkitType
            )}/${toolkit.id}`}
            id={`tv_link_to_${toolkit.id}`}
          >
            <span>
              <FontAwesomeIcon icon="copy" />
              {` Toolkit v.${toolkit.version}`}
            </span>
          </Link>
        )}
      </td>
      <td>
        {!isDraft ? (
          <div className="impactCategory-info">
            {ic !== 'N/A' ? (
              <ImpactBadge text={toolkit.impactCategory || toolkit.impactCategoryCalculated} />
            ) : (
              'N/A'
            )}
            {toolkit.impactCategory &&
              toolkit.impactCategoryCalculated &&
              toolkit.impactCategory !== toolkit.impactCategoryCalculated && (
                <>
                  <span
                    id={`impactCategory_icon_${toolkit.id}`}
                    className="impactCategory-info_icon"
                  >
                    <FontAwesomeIcon icon="info-circle" />
                  </span>
                  <span>Changed</span>
                  <UncontrolledTooltip
                    className="tooltip-impact"
                    placement="bottom"
                    target={`impactCategory_icon_${toolkit.id}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    <p>
                      <b>
                        {t('categoryChangedTooltip', {
                          catCalculated: toolkit.impactCategoryCalculated,
                          cat: toolkit.impactCategory,
                          by: toolkit.pdfCreatedBy,
                        })}
                      </b>
                    </p>
                    <p>
                      {t('rationale')}: <i>{toolkit.rationale}</i>
                    </p>
                    <p>
                      {t('comments')}: <i>{toolkit.rationalComments}</i>
                    </p>
                  </UncontrolledTooltip>
                </>
              )}
          </div>
        ) : (
          <div>-</div>
        )}
      </td>
      <td>
        {!isDraft ? (
          <div className="impactCategory-info">
            <RiskBadge text={toolkit.disasterRisk || toolkit.disasterRiskCalculated} />
            {toolkit.disasterRisk &&
              toolkit.disasterRiskCalculated &&
              toolkit.disasterRisk !== toolkit.disasterRiskCalculated && (
                <>
                  <span id={`disasterRisk_icon_${toolkit.id}`} className="impactCategory-info_icon">
                    <FontAwesomeIcon icon="info-circle" />
                  </span>
                  <span>Changed</span>
                  <UncontrolledTooltip
                    className="tooltip-impact"
                    placement="bottom"
                    target={`disasterRisk_icon_${toolkit.id}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    <p>
                      <b>
                        {t('categoryChangedTooltip', {
                          catCalculated: toolkit.disasterRiskCalculated,
                          cat: toolkit.disasterRisk,
                          by: toolkit.pdfCreatedBy,
                        })}
                      </b>
                    </p>
                    <p>
                      {t('rationale')}: <i>{toolkit.disasterRiskRationale}</i>
                    </p>
                    <p>
                      {t('comments')}: <i>{toolkit.disasterRiskRationaleComments}</i>
                    </p>
                  </UncontrolledTooltip>
                </>
              )}
          </div>
        ) : (
          <div>-</div>
        )}
      </td>
      <td>
        {!isDraft ? (
          <div className="esrr-badge esrr-info">
            {toolkit.esrr ? <EsrrBadge text={toolkit.esrr} /> : 'N/A'}
            {toolkit.esrr !== null &&
              toolkit.esrrCalculated !== null &&
              toolkit.esrr !== toolkit.esrrCalculated && (
                <>
                  <span id={`esrr_icon_${toolkit.id}`} className="esrr-info_icon">
                    <FontAwesomeIcon icon="info-circle" />
                  </span>
                  <span> Changed</span>
                  <UncontrolledTooltip
                    className="tooltip-impact"
                    placement="bottom"
                    target={`esrr_icon_${toolkit.id}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    <p>
                      <b>
                        {t('categoryChangedTooltip', {
                          catCalculated: toolkit.esrrCalculated
                            ? toolkit.esrrCalculated.substring(1)
                            : '',
                          cat: toolkit.esrr ? toolkit.esrr.substring(1) : '',
                          by: toolkit.pdfCreatedBy,
                        })}
                      </b>
                    </p>
                    <p>
                      {t('rationale')}: <i>{toolkit.esrrRationale}</i>
                    </p>
                    <p>
                      {t('comments')}: <i>{toolkit.esrrRationaleComments}</i>
                    </p>
                  </UncontrolledTooltip>
                </>
              )}
          </div>
        ) : (
          <div>-</div>
        )}
      </td>
      <td>{toolkit.createdBy}</td>
      <td>{dateTimeFormat(toolkit.modifiedOn)}</td>
      <td>
        {!isDraft && pdfOptions ? (
          <>
            {pdfOptions.length > 1 ? (
              <CustomDropDown
                key={`tv_dd_download_${toolkit.id}`}
                text=""
                icon="file-download"
                className="dropdown_tertiary"
                outline={false}
                color="tertiary"
              >
                <div className="dropdown-content">
                  <CheckboxList checkValues={pdfOptions} handleChange={handleChange} />
                </div>
                <DropdownItem divider />
                <div className="dropdown-button">
                  <UIButton
                    key={`tv_btn_download_${toolkit.id}`}
                    color="primary"
                    block
                    onClick={() => handleDownloadClick(toolkit.id)}
                  >
                    Download
                  </UIButton>
                </div>
              </CustomDropDown>
            ) : (
              <>
                <Button
                  key={`tv_btn_download_${toolkit.id}`}
                  className="btn-tertiary no-padding"
                  htmlId={`tv_btn_download_${toolkit.id}`}
                  icon="file-download"
                  text=""
                  onClick={() => handleDownloadClick(toolkit.id)}
                />

                {handleDeleteClick && (
                  <Button
                    key={`tv_btn_delete_${toolkit.id}`}
                    className="btn-tertiary no-padding"
                    htmlId={`tv_btn_delete_${toolkit.id}`}
                    icon="trash-alt"
                    text=""
                    onClick={() => {
                      if (handleDeleteClick) {
                        handleDeleteClick(toolkit.id);
                      }
                    }}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <>
            {handleDeleteClick ? (
              <Button
                key={`tv_btn_delete_${toolkit.id}`}
                className="btn-tertiary no-padding"
                htmlId={`tv_btn_delete_${toolkit.id}`}
                icon="trash-alt"
                text=""
                onClick={() => {
                  if (handleDeleteClick) {
                    handleDeleteClick(toolkit.id);
                  }
                }}
              />
            ) : (
              <div>-</div>
            )}
          </>
        )}
      </td>
    </tr>
  );
};

export default EspfToolkitVersion;
